<script setup>
import { computed } from 'vue';

import WootLabel from 'dashboard/components-next/label/Label.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';

defineProps({
  items: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  meta: { type: Object, default: () => ({}) },
});

defineEmits(['pageChange']);

const {
  t,
  actionReceiptProviders,
  formatDate,
  formatDuration,
  formatPercent,
  formatStatus,
  labelColor,
  ruleRowState,
  ruleSloReason,
  usersLabel,
} = useAutomationOperationsFormatters();

const ruleHealthHeaders = computed(() => [
  t('AUTOMATION.OPERATIONS.RULES.TABLE_HEADER.RULE'),
  t('AUTOMATION.OPERATIONS.RULES.TABLE_HEADER.EVENT'),
  t('AUTOMATION.OPERATIONS.RULES.TABLE_HEADER.EXECUTIONS'),
  t('AUTOMATION.OPERATIONS.RULES.TABLE_HEADER.SLO'),
  t('AUTOMATION.OPERATIONS.RULES.TABLE_HEADER.LATENCY'),
  t('AUTOMATION.OPERATIONS.RULES.TABLE_HEADER.ACTIONS'),
]);
</script>

<template>
  <BaseTable
    :headers="ruleHealthHeaders"
    :items="items"
    :loading="isLoading"
    :loading-message="$t('AUTOMATION.OPERATIONS.RULES.LOADING')"
    :no-data-message="$t('AUTOMATION.OPERATIONS.RULES.EMPTY')"
  >
    <template #row="{ items: rows }">
      <BaseTableRow
        v-for="rule in rows"
        :key="`${rule.version?.id || 'missing'}:${rule.event_name}`"
        :item="rule"
        :state="ruleRowState(rule)"
      >
        <template #default>
          <BaseTableCell class="min-w-56">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ rule.rule?.name || '-' }}
              </span>
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-body-main text-n-slate-11">
                  {{
                    $t('AUTOMATION.OPERATIONS.RULES.VERSION', {
                      version: rule.version?.version_number || '-',
                    })
                  }}
                </span>
                <WootLabel
                  :label="formatStatus(rule.version?.status)"
                  :color="labelColor(rule.version?.status)"
                  compact
                />
              </div>
              <span
                v-if="usersLabel(rule.ownership?.primary)"
                class="truncate text-body-main text-n-slate-11"
              >
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.OWNERS', {
                    owners: usersLabel(rule.ownership?.primary),
                  })
                }}
              </span>
              <span
                v-if="usersLabel(rule.ownership?.on_call)"
                class="truncate text-body-main text-n-slate-11"
              >
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.ON_CALL', {
                    owners: usersLabel(rule.ownership?.on_call),
                  })
                }}
              </span>
              <span
                v-if="rule.ownership?.runbook_key"
                class="truncate text-body-main text-n-slate-11"
              >
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.RUNBOOK', {
                    key: rule.ownership.runbook_key,
                  })
                }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-40">
            <span class="text-body-main text-n-slate-12">
              {{ rule.event_name }}
            </span>
          </BaseTableCell>
          <BaseTableCell class="min-w-48">
            <div class="flex flex-wrap gap-x-3 gap-y-1 text-body-main">
              <span class="tabular-nums text-n-slate-12">
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.EXEC_TOTAL', {
                    count: rule.executions?.total_count || 0,
                  })
                }}
              </span>
              <span class="tabular-nums text-n-slate-11">
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.EXEC_SUCCEEDED', {
                    count: rule.executions?.succeeded_count || 0,
                  })
                }}
              </span>
              <span class="tabular-nums text-n-slate-11">
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.EXEC_FAILED', {
                    count: rule.executions?.failed_or_blocked_count || 0,
                  })
                }}
              </span>
              <span class="tabular-nums text-n-slate-11">
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.EXEC_ACTIVE', {
                    count: rule.executions?.active_count || 0,
                  })
                }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-48">
            <div class="flex min-w-0 flex-col gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <WootLabel
                  :label="formatStatus(rule.slo?.status)"
                  :color="labelColor(rule.slo?.status)"
                  compact
                />
                <span class="tabular-nums text-body-main text-n-slate-12">
                  {{ formatPercent(rule.slo?.success_ratio) }}
                </span>
              </div>
              <span class="line-clamp-1 text-body-main text-n-slate-11">
                {{ ruleSloReason(rule) }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-44">
            <div class="flex min-w-0 flex-col gap-1 text-body-main">
              <span class="tabular-nums text-n-slate-12">
                {{
                  $t('AUTOMATION.OPERATIONS.RULES.P95', {
                    duration: formatDuration(
                      rule.executions?.p95_duration_seconds
                    ),
                  })
                }}
              </span>
              <span class="text-n-slate-11">
                {{ formatDate(rule.executions?.latest_success_at) }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-72">
            <div
              v-if="rule.actions?.length"
              class="flex min-w-0 flex-col divide-y divide-n-weak"
            >
              <div
                v-for="action in rule.actions.slice(0, 3)"
                :key="`${rule.version?.id}:${action.action_ref}`"
                class="grid gap-1 py-2 first:pt-0 last:pb-0"
              >
                <div class="flex min-w-0 items-center gap-2">
                  <span class="truncate text-body-main text-n-slate-12">
                    {{ action.action_name }}
                  </span>
                  <span class="text-body-main text-n-slate-11">
                    {{ action.action_ref }}
                  </span>
                </div>
                <div
                  class="flex flex-wrap gap-x-3 gap-y-1 text-body-main text-n-slate-11"
                >
                  <span class="tabular-nums">
                    {{
                      $t('AUTOMATION.OPERATIONS.RULES.ACTION_ATTEMPTS', {
                        count: action.attempts_count || 0,
                      })
                    }}
                  </span>
                  <span class="tabular-nums">
                    {{
                      $t('AUTOMATION.OPERATIONS.RULES.ACTION_FAILED', {
                        count: action.failed_count || 0,
                      })
                    }}
                  </span>
                  <span v-if="actionReceiptProviders(action)" class="truncate">
                    {{ actionReceiptProviders(action) }}
                  </span>
                </div>
              </div>
            </div>
            <span v-else class="text-body-main text-n-slate-11">
              {{ $t('AUTOMATION.OPERATIONS.RULES.NO_ACTIONS') }}
            </span>
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>
  <PaginationFooter
    v-if="meta.total_count"
    :current-page="Number(meta.page)"
    :total-items="Number(meta.total_count)"
    :items-per-page="Number(meta.per_page)"
    class="!px-0"
    @update:current-page="$emit('pageChange', $event)"
  />
</template>
